/*
 * Kassakoopje (order bump) in de winkelmand.
 *
 * Voegt een product toe via de Ajax Cart API en hangt er de verborgen line item
 * property `_kassakoopje` aan. Die property staat mee op de orderregel, zodat
 * achteraf exact te meten is welke omzet uit het kassakoopje komt.
 *
 * Events op window.dataLayer:
 *   kassakoopje_view  - blok is minstens half in beeld geweest (1x per weergave)
 *   kassakoopje_add   - klant heeft een kassakoopje toegevoegd
 */

const KK_PROPERTY = '_kassakoopje';

/* Per pageload één impressie per plek. Het blok wordt na elke cart-update
   opnieuw gerenderd, zonder dit zou elke refresh een view bijtellen. */
const kkTrackedViews = new Set();

/* Heeft de klant de lijst opengeklapt? Onthouden over een re-render heen,
   anders klapt hij weer dicht zodra er iets aan de mand verandert. */
let kkUitgeklapt = false;

function kkContext() {
  return document.body.classList.contains('cart-drawer-is-open') ? 'drawer' : 'cart_page';
}

function kkSectionsToRender() {
  const sections = [];
  const items = document.getElementById('main-cart-items');
  if (items && items.dataset.id) {
    sections.push({
      id: 'main-cart-items',
      section: items.dataset.id,
      selector: '.js-cart-item-contents',
    });
  }

  const footer = document.getElementById('main-cart-footer');
  if (footer && footer.dataset.id) {
    sections.push({
      id: 'main-cart-footer',
      section: footer.dataset.id,
      selector: '.js-cart-footer-contents',
    });
  }

  sections.push({
    id: 'cart-icon-bubble',
    section: 'cart-icon-bubble',
    selector: '[data-cart-icon-bubble]',
  });

  return sections;
}

function kkInnerHTML(html, selector) {
  if (!html) return null;
  const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  return found ? found.innerHTML : null;
}

function kkTrack(payload) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  document.dispatchEvent(new CustomEvent(payload.event, { detail: payload }));
}

/* Eén kaartje in het kassakoopje-blok. */
class KassakoopjeCard extends HTMLElement {
  connectedCallback() {
    if (this.isBound) return;
    this.isBound = true;

    this.button = this.querySelector('[data-kk-add]');
    if (this.button) {
      this.button.addEventListener('click', this.onClick.bind(this));
    }
  }

  async onClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const variantId = Number(this.dataset.variantId);
    if (!variantId || this.isBusy) return;

    this.isBusy = true;
    this.classList.add('is-loading');

    const context = kkContext();
    const sections = kkSectionsToRender();
    const properties = {};
    properties[KK_PROPERTY] = context;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: properties }],
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Kassakoopje kon niet toegevoegd worden');
      }

      kkTrack({
        event: 'kassakoopje_add',
        kk_context: context,
        kk_product_id: this.dataset.productId,
        kk_variant_id: this.dataset.variantId,
        kk_product_title: this.dataset.title,
        kk_value: Number(this.dataset.price || 0) / 100,
        currency: this.dataset.currency || 'EUR',
      });

      if (data.sections) {
        sections.forEach((section) => {
          const host = document.getElementById(section.id);
          if (!host) return;

          const target = host.querySelector(section.selector) || host;
          const markup = kkInnerHTML(data.sections[section.section], section.selector);
          if (markup !== null) target.innerHTML = markup;
        });
      }

      document.dispatchEvent(new CustomEvent('kassakoopje:rendered'));
    } catch (error) {
      console.error('[kassakoopje]', error);
      this.classList.remove('is-loading');
      this.isBusy = false;
    }
  }
}

if (!customElements.get('kassakoopje-card')) {
  customElements.define('kassakoopje-card', KassakoopjeCard);
}

/* Het blok rond de kaartjes. Meet één impressie zodra het in beeld komt. */
class KassakoopjeBlock extends HTMLElement {
  connectedCallback() {
    this.bindMore();

    if (this.hasTracked || !('IntersectionObserver' in window)) return;
    if (kkTrackedViews.has(kkContext())) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || this.hasTracked) return;

          const context = kkContext();
          if (kkTrackedViews.has(context)) {
            observer.disconnect();
            return;
          }

          this.hasTracked = true;
          kkTrackedViews.add(context);
          observer.disconnect();

          kkTrack({
            event: 'kassakoopje_view',
            kk_context: context,
            kk_items: Number(this.dataset.count || 0),
          });
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(this);
  }

  /* Op mobiel staan alleen de eerste drie producten in beeld. Deze knop klapt
     de rest uit. Eenmaal opengeklapt blijft dat zo, ook na een cart-update. */
  bindMore() {
    if (kkUitgeklapt) this.classList.add('kk--open');

    const knop = this.querySelector('[data-kk-more]');
    if (!knop || knop.dataset.kkBound) return;
    knop.dataset.kkBound = '1';

    knop.addEventListener('click', () => {
      kkUitgeklapt = true;
      this.classList.add('kk--open');

      kkTrack({
        event: 'kassakoopje_meer',
        kk_context: kkContext(),
        kk_items: Number(this.dataset.count || 0),
      });
    });
  }
}

if (!customElements.get('kassakoopje-block')) {
  customElements.define('kassakoopje-block', KassakoopjeBlock);
}
